import AdminNav from '@/components/admin-nav';
import { admin_holiday_path, admin_holidays_path } from '@/rails/routes';
import { formatShortDate, weekdayName } from '@/pages/reports/lib/format';
import type { AdminHolidaysIndex, WorkspacePageProps } from '@/types';
import { Badge } from '@astryxdesign/core/Badge';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { DateInput } from '@astryxdesign/core/DateInput';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { Icon } from '@astryxdesign/core/Icon';
import { IconButton } from '@astryxdesign/core/IconButton';
import { Text } from '@astryxdesign/core/Text';
import { TextInput } from '@astryxdesign/core/TextInput';
import { VStack } from '@astryxdesign/core/VStack';
import { Form, router, usePage } from '@inertiajs/react';
import { CalendarOff, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useDirtyGuard } from '@/components/dirty-guard';

type Props = WorkspacePageProps & AdminHolidaysIndex;

/** ISO date string `YYYY-MM-DD`, matching Astryx DateInput's value type. */
type ISODateString = `${number}${number}${number}${number}-${number}${number}-${number}${number}`;

const Index = () => {
  const { holidays } = usePage<Props>().props;
  const [newDate, setNewDate] = useState('');
  const [newLabel, setNewLabel] = useState('');
  // Avisar antes de salir con un feriado nuevo en curso.
  useDirtyGuard(newDate !== '' || newLabel !== '');

  return (
    <VStack gap={6} width="100%" className="iorio-holidays-page">
      <AdminNav />

      <Card padding={6} className="iorio-holidays-card">
        <VStack gap={5}>
          <HStack hAlign="between" vAlign="start" gap={3} wrap="wrap">
            <VStack gap={1}>
              <HStack gap={2} vAlign="center">
                <span className="iorio-holidays-icon" aria-hidden="true">
                  <Icon icon={CalendarOff} size="sm" />
                </span>
                <Heading level={2}>Días cargados</Heading>
              </HStack>
              <Text type="supporting" color="secondary">
                Cada ítem se excluye del calendario laboral del equipo.
              </Text>
            </VStack>
            <Badge label={`${holidays.length} totales`} variant="red" />
          </HStack>

          {holidays.length === 0 ? (
            <section
              className="iorio-holidays-empty"
              aria-label="Estado vacío de días no laborables"
            >
              <EmptyState
                title="Todavía no hay días no laborables"
                description="Agregá feriados o días puntuales para que el calendario del parte diario los saltee."
                icon={
                  <span className="iorio-holidays-icon" aria-hidden="true">
                    <Icon icon={CalendarOff} size="sm" />
                  </span>
                }
                isCompact
              />
            </section>
          ) : (
            <ul className="iorio-holidays-list" aria-label="Días no laborables cargados">
              {holidays.map((holiday) => (
                <li key={holiday.id} className="iorio-holidays-row">
                  <HStack hAlign="between" vAlign="center" gap={3} wrap="wrap">
                    <HStack gap={3} vAlign="center" wrap="wrap" className="iorio-holidays-row-main">
                      <Text type="body" weight="bold" className="iorio-holidays-date">
                        {formatShortDate(holiday.date)}
                      </Text>
                      <Text type="supporting" color="secondary">
                        {weekdayName(holiday.date)}
                      </Text>
                      <Text type="body" color="secondary" className="iorio-holidays-label">
                        {holiday.label}
                      </Text>
                    </HStack>

                    <IconButton
                      label={`Eliminar ${holiday.label}`}
                      tooltip="Eliminar"
                      icon={<X size={14} />}
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        router.delete(admin_holiday_path(holiday.id), { preserveScroll: true })
                      }
                    />
                  </HStack>
                </li>
              ))}
            </ul>
          )}
        </VStack>
      </Card>

      <Card padding={6} className="iorio-holidays-form-card">
        <Form
          method="post"
          action={admin_holidays_path()}
          resetOnSuccess
          onSuccess={() => {
            setNewDate('');
            setNewLabel('');
          }}
        >
          {({ processing }) => (
            <VStack gap={5}>
              <VStack gap={2}>
                <HStack gap={2} vAlign="center">
                  <span className="iorio-holidays-icon" aria-hidden="true">
                    <Icon icon={Plus} size="sm" />
                  </span>
                  <Heading level={2}>Agregar día no laborable</Heading>
                </HStack>
                <Text type="supporting" color="secondary">
                  Elegí la fecha y escribí un motivo claro para que el equipo lo reconozca.
                </Text>
              </VStack>

              <HStack gap={4} vAlign="end" wrap="wrap">
                <input type="hidden" name="date" value={newDate} />

                <div className="iorio-holidays-field">
                  <DateInput
                    label="Fecha"
                    value={(newDate || undefined) as ISODateString | undefined}
                    onChange={(value) => setNewDate(value ?? '')}
                    placeholder="Seleccionar fecha"
                    isRequired
                  />
                </div>

                <div className="iorio-holidays-field iorio-holidays-field-wide">
                  <TextInput
                    label="Motivo"
                    htmlName="label"
                    placeholder="Ej: Día de la Independencia"
                    value={newLabel}
                    onChange={setNewLabel}
                    size="md"
                    startIcon={CalendarOff}
                    isRequired
                  />
                </div>

                <Button label="Agregar" variant="primary" type="submit" isLoading={processing} />
              </HStack>
            </VStack>
          )}
        </Form>
      </Card>
    </VStack>
  );
};

export default Index;
